import { changeChord, moveChord, removeChord } from "@bandstand/core/arrangeChords";
import type { ChordRef, Place } from "@bandstand/core/arrangeChords";
import { withBass } from "@bandstand/core/chord";
import { readSheet, transposeText } from "@bandstand/core/chordPro";
import type { ChordSheet } from "@bandstand/core/ChordSheet";
import type { SaveChoice } from "@bandstand/core/Library";
import type { Sheet } from "@bandstand/core/chordPro";
import { action, computed, observable, runInAction } from "mobx";

import type { LibraryStore } from "./LibraryStore";
import type { EditScreenName, From, NavigationStore } from "./NavigationStore";

/** A chord sheet being edited. Unlike viewing settings, these changes are saved into the chord sheet. */
export class EditorStore {
    @observable accessor text = "";

    @observable accessor original = "";

    @observable accessor chordSheetId: string | undefined = undefined;

    /** The text before each change that undo can take back, the last one first to go. */
    @observable accessor undos: string[] = [];

    constructor(
        private readonly library: LibraryStore,
        private readonly navigation: NavigationStore,
    ) {}

    @computed get from(): From {
        const screen = this.navigation.screen;

        return screen.name === "edit" || screen.name === "arrange" ? screen.from : { kind: "songs" };
    }

    @computed get chordSheet(): ChordSheet | undefined {
        return this.library.chordSheet(this.chordSheetId);
    }

    @computed get changed(): boolean {
        return this.text !== this.original;
    }

    @computed get preview(): Sheet {
        return readSheet(this.text);
    }

    /** Starts editing a chord sheet afresh, by arranging its chords or as text. */
    @action
    start(chordSheetId: string, from: From, how: EditScreenName): void {
        this.chordSheetId = undefined;
        this.navigation.go({ name: how, chordSheetId, from });
    }

    /** Switches between arranging and editing as text, keeping the changes. */
    switchTo(how: EditScreenName): void {
        this.navigation.replace({ name: how, chordSheetId: this.chordSheetId!, from: this.from });
    }

    /** Loads the chord sheet, unless it's already being edited, like after switching between arranging and text. */
    async load(chordSheetId: string): Promise<void> {
        if (this.chordSheetId === chordSheetId) return;

        const text = await this.library.text(chordSheetId);

        runInAction(() => {
            this.chordSheetId = chordSheetId;
            this.text = text;
            this.original = text;
            this.undos = [];
        });
    }

    /** Typing has its own undo, so after typing, undo can't take back what came before. */
    @action
    setText(text: string): void {
        this.text = text;
        this.undos = [];
    }

    /** Transposes the chord sheet itself: every chord and the key in the text change. */
    transpose(steps: number): void {
        this.change(transposeText(this.text, steps));
    }

    moveChord(chord: ChordRef, to: Place): void {
        this.change(moveChord(this.text, chord, to));
    }

    removeChord(chord: ChordRef): void {
        this.change(removeChord(this.text, chord));
    }

    /** Gives a chord another bass note, like C/E, or takes it off. */
    setBass(chord: ChordRef, bass: string | undefined): void {
        this.change(changeChord(this.text, chord, (name) => withBass(name, bass)));
    }

    /** Takes back the last change. */
    @action
    undo(): void {
        const text = this.undos.at(-1);

        if (text === undefined) return;

        this.text = text;
        this.undos = this.undos.slice(0, -1);
    }

    /** Saves in one of the three ways, then shows the chord sheet that holds the changes. */
    async save(choice: SaveChoice): Promise<void> {
        const from = this.from;
        const place = from.kind === "setlist" ? { setlistId: from.setlistId, index: from.index } : undefined;
        const saved = await this.library.change((library) => library.save(this.chordSheetId!, this.text, choice, place));

        this.navigation.replace({ name: "sheet", chordSheetId: saved.id, from });
    }

    /** Stops editing without saving. */
    cancel(): void {
        this.navigation.replace({ name: "sheet", chordSheetId: this.chordSheetId!, from: this.from });
    }

    /** Changes the text in a way undo can take back. */
    @action
    private change(text: string): void {
        if (text === this.text) return;

        this.undos = [...this.undos, this.text];
        this.text = text;
    }
}
